import { calculateAttorneyFee } from "@/lib/utils/attorneyFeeUtils";
import { AttorneyFeeSelect } from "@/components/features/matter/AttorneyFeeSelect/AttorneyFeeSelect";
import { SelectDocumentModal } from "@/components/features/matter/file-repo/signature-requests/SelectDocumentModal";
import { SelectedInsuranceCard } from "@/components/features/matter/settlements/SelectedInsuranceCard";
import { SelectMatterInsuranceFormField } from "@/components/features/matter/settlements/SelectMatterInsuranceFormField";
import { SelectFirmFileModal } from "@/components/features/settings/firm/SelectFirmFileModal";
import {
  ActionConfirmationModal,
  AppModal,
  Button,
  DateFormField,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormSubmitButton,
  LoadingSpinner,
  SimpleSelectFormField,
  SwitchFormField,
  TextAreaFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { CheckboxFormField } from "@/components/ui/FormFields/CheckboxFormField/CheckboxFormField";
import { CurrencyFormField } from "@/components/ui/FormFields/CurrencyFormField/CurrencyFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/shadcn/DropdownMenu/DropdownMenu";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { searchContactsQueryOptions } from "@/lib/data/react-query";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  useCreateMatterFile,
  useCreateMatterFileReference,
  useMergeMatterFiles,
} from "@/lib/data/react-query/matters/matter-file";
import {
  useCreateMatterSettlementDemand,
  useDeleteMatterSettlementDemand,
  useUpdateMatterSettlementDemand,
} from "@/lib/data/react-query/matters/matter-settlement-demand";
import {
  useCreateMatterSettlementOffer,
  useDeleteMatterSettlementOffer,
  useUpdateMatterSettlementOffer,
} from "@/lib/data/react-query/matters/matter-settlement-offer";
import {
  createMatterSettlementDemandPackageSegmentV1,
  deleteMatterSettlementDemandPackageSegmentV1,
  updateMatterSettlementDemandPackageSegmentV1,
} from "@/lib/data/requests/matters/matter-settlement-demand-package-segment";
import { generateS3ItemPreSignedUrlV1 } from "@/lib/data/requests/s3";
import {
  MatterInsuranceSchema,
  MatterSettlementDemandPackageSegmentFormSchema,
  MatterSettlementDemandSchema,
  MatterSettlementOfferSchema,
} from "@/lib/data/schemas/matters/MatterSchema";
import { MATTER_SETTLEMENT_MONEY_STATUS_OPTIONS } from "@/lib/data/static/matters/settlementStatics";
import { MATTER_DEFAULT_ATTORNEY_FEE_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { queryKeys } from "@/lib/data/static/query-keys";
import {
  SLIDEOVER_DESCRIPTION,
  SLIDEOVER_TITLE,
} from "@/lib/data/static/slideoverStatics";
import type {
  FirmFile,
  MatterFile,
  MatterInsurance,
  MatterSettlementDemand,
  MatterSettlementDemandPackageSegment,
  MatterSettlementOffer,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { getInsuranceContactConnection } from "@/lib/utils/connections/insurance-contact-connections";
import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { formatDate } from "@/lib/utils/format";
import { getFileNameParts } from "@/lib/utils/matterFile";
import { isAnyDirtyInFieldTree } from "@/lib/utils/react-hook-form/dirty-field-tree";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { useModals } from "@/providers/ModalProvider";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import dayjs from "dayjs";
import { isNil, omitBy, uniq } from "lodash";
import {
  AlertCircleIcon,
  ChevronDown,
  FilesIcon,
  GripVertical,
  Plus,
  SquareArrowOutUpRight,
  WandSparkles,
  X,
} from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

type MatterSettlementOfferWithReceivedDate = MatterSettlementOffer & {
  dateReceived?: string | null;
};

interface AddOrEditSettlementDemandModalProps {
  title: string;
  description?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  editing?: boolean;
  matterId: string;
  settlementId?: string;
  negotiationType: "Demand" | "Offer";
  selectedInsurance?: MatterInsurance;
  isMedpay?: boolean;
  coverageType?: string;
  initialData?:
    | Partial<MatterSettlementDemand>
    | Partial<MatterSettlementOffer>;
}

type DemandPackageExhibitFormValue = {
  type: "file";
  clientId: string;
  segmentId?: string;
  segmentDateModified?: string;
  fileId: string;
};

const segmentsToDocuments = (
  segments: MatterSettlementDemandPackageSegment[],
): DemandPackageExhibitFormValue[] => {
  if (!segments.length) return [];

  const sortedSegments = [...segments].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );

  const documents: DemandPackageExhibitFormValue[] = [];
  sortedSegments.forEach((segment, index) => {
    if (!segment.fileId) return;
    const clientId = segment.id ?? `${segment.fileId}-${index}`;
    documents.push({
      type: "file",
      clientId,
      segmentId: segment.id ?? undefined,
      segmentDateModified: segment.dateModified,
      fileId: segment.fileId,
    });
  });

  return documents;
};

const documentsToSegments = (documents: DemandPackageExhibitFormValue[]) => {
  const segments: z.infer<typeof packageSegmentFormSchema>[] = [];

  documents.forEach((document) => {
    segments.push({
      id: document.segmentId,
      templateInput: null,
      fileId: document.fileId,
      templateId: null,
    });
  });

  return segments;
};

const buildDemandPackageSnapshot = ({
  titlePageFileId,
  exhibits,
}: {
  titlePageFileId?: string | null;
  exhibits: DemandPackageExhibitFormValue[];
}) =>
  JSON.stringify({
    titlePageFileId: titlePageFileId ?? null,
    exhibitFileIds: exhibits.map((exhibit) => exhibit.fileId),
  });

const TITLE_PAGE_SEGMENT_ORDER = 100;
const DEMAND_SEGMENT_ORDER_INCREMENT = 100;
const SETTLEMENT_COVERAGE_TYPES = [
  "Bodily Injury",
  "Property Damage",
  "UM",
  "UIM",
  "MedPay",
] as const;
const packageSegmentFormSchema =
  MatterSettlementDemandPackageSegmentFormSchema.pick({
    id: true,
    order: true,
    templateInput: true,
    fileId: true,
    templateId: true,
  });

const generatePackageFormSchema = z.object({
  packageName: z.string().trim().min(1, {
    message: "Package name is required",
  }),
});

const createSettlementDemandFormSchema = (
  negotiationType: "Demand" | "Offer",
) => {
  if (negotiationType === "Demand") {
    return MatterSettlementDemandSchema.omit({
      connections: true,
      connectionsNextCursor: true,
      dateCreated: true,
      dateModified: true,
      matterId: true,
      id: true,
      acceptedWithIssues: true,
    })
      .extend({
        insuranceId: MatterInsuranceSchema.shape.id
          .optional()
          .refine((value) => value, {
            message: "Insurance is required",
          }),
        settlementDemandPackageSegments: packageSegmentFormSchema
          .array()
          .optional(),
      })
      .superRefine((data, ctx) => {
        if (!data.dateExpires) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Date expires is required",
            path: ["dateExpires"],
          });
        }
      });
  } else {
    return MatterSettlementOfferSchema.omit({
      connections: true,
      connectionsNextCursor: true,
      dateCreated: true,
      dateModified: true,
      matterId: true,
      id: true,
      acceptedWithIssues: true,
    }).extend({
      insuranceId: MatterInsuranceSchema.shape.id
        .optional()
        .refine((value) => value, {
          message: "Insurance is required",
        }),
      issues: z.boolean().optional(),
      settlementDemandPackageSegments: packageSegmentFormSchema
        .array()
        .optional(),
    });
  }
};

const createDefaultFormValues = (negotiationType: "Demand" | "Offer") => {
  const baseValues = {
    insuranceId: undefined,
    settlementDemandPackageSegments: [],
  };

  if (negotiationType === "Demand") {
    return {
      ...baseValues,
      policyLimit: false,
      amount: null,
      dateExpires: "",
      details: undefined,
      dateSent: dayjs().format("YYYY-MM-DD"),
      attorneyFeePercentage: null,
      attorneyFeeAmount: null,
      coverageType: undefined,
      accepted: false,
      acceptedDate: "",
      medPayRefund: false,
      medPayRefundAmount: null,
    };
  } else {
    return {
      ...baseValues,
      policyLimit: false,
      amount: null,
      details: undefined,
      dateReceived: dayjs().format("YYYY-MM-DD"),
      accepted: false,
      acceptedDate: "",
      issues: false,
      status: "Not Received",
      attorneyFeePercentage: null,
      attorneyFeeAmount: null,
      coverageType: undefined,
      medPay: false,
      medPayRefund: false,
      medPayRefundAmount: null,
    };
  }
};

export const AddOrEditSettlementDemandModal = ({
  title,
  description,
  open,
  setOpen,
  editing,
  matterId,
  settlementId,
  negotiationType,
  selectedInsurance,
  isMedpay,
  coverageType,
  initialData,
}: AddOrEditSettlementDemandModalProps) => {
  const { updateSlideoverPayload } = useModals();
  const { openGenerateDocumentModal, openFileModal } = useGlobalModal();
  const [isDeleting, setIsDeleting] = useState(false);
  const { hasActionPermission } = useUser();
  const queryClient = useQueryClient();
  const settlementDemandFormId = useId();
  const generatePackageFormId = useId();
  const settlementPermissionKey =
    negotiationType === "Demand"
      ? ACTION_PERMISSION_KEYS.matterSettlementDemand
      : ACTION_PERMISSION_KEYS.matterSettlementOffer;
  const hasUpdatePermission = hasActionPermission(
    settlementPermissionKey,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasCreatePermission = hasActionPermission(
    settlementPermissionKey,
    ACTION_PERMISSION_VALUES.create,
  );
  const canEditSettlement = editing ? hasUpdatePermission : hasCreatePermission;
  const canViewMatterFiles = Boolean(true);
  const canCreateMatterFiles = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterFile,
    ACTION_PERMISSION_VALUES.create,
  );
  const canCreateDemandPackageSegments = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterSettlementDemandPackageSegment,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdateDemandPackageSegments = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterSettlementDemandPackageSegment,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDeleteDemandPackageSegments = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterSettlementDemandPackageSegment,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canViewFirmFiles = Boolean(true);
  const canUseFirmFiles = canViewFirmFiles && canCreateMatterFiles;

  // Fetch matter data
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );

  const matter = matterData.data;
  const contactIds = useMemo(() => {
    return uniq(
      getMatterContactConnections(matter)
        .map((connection) => connection.contactId)
        .filter((contactId): contactId is string => Boolean(contactId)),
    );
  }, [matter]);
  const { data: contactsData, isLoading: isContactsLoading } = useQuery({
    ...searchContactsQueryOptions({
      contactIds,
      matterIds: [matterId],
    }),
    enabled: contactIds.length > 0,
  });
  const contacts = useMemo(
    () => (contactIds.length > 0 ? (contactsData?.data ?? []) : []),
    [contactIds.length, contactsData?.data],
  );
  const mergeMatterFiles = useMergeMatterFiles();
  const createMatterFile = useCreateMatterFile();
  const createDemandFileReference = useCreateMatterFileReference();

  // Get the specific settlement if editing
  const selectedSettlement = settlementId
    ? negotiationType === "Demand"
      ? matter.settlementDemands.find((demand) => demand.id === settlementId)
      : matter.settlementOffers.find((offer) => offer.id === settlementId)
    : undefined;
  const settlementInitialValues = useMemo<
    MatterSettlementDemand | MatterSettlementOffer | undefined
  >(() => {
    if (!selectedSettlement && !initialData) {
      return selectedSettlement;
    }
    return {
      ...(selectedSettlement ?? {}),
      ...(initialData ?? {}),
    } as MatterSettlementDemand | MatterSettlementOffer;
  }, [initialData, selectedSettlement]);
  const normalizedSettlementInitialValues = useMemo(() => {
    if (!settlementInitialValues) {
      return settlementInitialValues;
    }
    if (negotiationType === "Demand") {
      const demandValues = settlementInitialValues as MatterSettlementDemand;
      return {
        ...demandValues,
        accepted: demandValues.accepted ?? false,
        medPayRefund: demandValues.medPayRefund ?? false,
        policyLimit: demandValues.policyLimit ?? false,
      };
    }
    const offerValues =
      settlementInitialValues as MatterSettlementOfferWithReceivedDate;
    const coverageType =
      offerValues.coverageType ?? (offerValues.medPay ? "MedPay" : undefined);
    return {
      ...offerValues,
      dateReceived: offerValues.dateReceived || "",
      coverageType: coverageType,
      issues: offerValues.acceptedWithIssues ?? false,
    };
  }, [negotiationType, settlementInitialValues]);

  // Type guards for selectedSettlement
  const isDemandSettlement = (
    settlement: unknown,
  ): settlement is MatterSettlementDemand =>
    settlement !== null &&
    settlement !== undefined &&
    typeof settlement === "object" &&
    "id" in settlement &&
    "dateSent" in settlement;
  const isOfferSettlement = (
    settlement: unknown,
  ): settlement is MatterSettlementOffer =>
    settlement !== null &&
    settlement !== undefined &&
    typeof settlement === "object" &&
    "id" in settlement &&
    "dateReceived" in settlement;

  const selectedDemandId = useMemo(() => {
    if (negotiationType !== "Demand") {
      return undefined;
    }

    if (isDemandSettlement(selectedSettlement)) {
      return selectedSettlement.id;
    }

    if (settlementId) {
      return settlementId;
    }

    if (initialData && "id" in initialData && initialData.id) {
      return initialData.id;
    }

    return undefined;
  }, [initialData, negotiationType, selectedSettlement, settlementId]);

  const existingDemandPackageSegments = useMemo(() => {
    if (negotiationType !== "Demand") {
      return [];
    }
    if (!selectedDemandId) {
      return [];
    }
    return matter.settlementDemandPackageSegments.filter(
      (segment) => segment.settlementDemandId === selectedDemandId,
    );
  }, [
    matter.settlementDemandPackageSegments,
    negotiationType,
    selectedDemandId,
  ]);

  const existingSegmentsById = useMemo(() => {
    const map = new Map<string, MatterSettlementDemandPackageSegment>();
    existingDemandPackageSegments.forEach((segment) => {
      if (segment.id) {
        map.set(segment.id, segment);
      }
    });
    return map;
  }, [existingDemandPackageSegments]);

  const titlePageSegments = useMemo(
    () =>
      existingDemandPackageSegments.filter(
        (segment) => Number(segment.order ?? 0) === TITLE_PAGE_SEGMENT_ORDER,
      ),
    [existingDemandPackageSegments],
  );

  const titlePageSegment = useMemo(() => {
    if (!titlePageSegments.length) return undefined;
    return [...titlePageSegments].sort((a, b) => {
      const aTime = a.dateModified ? new Date(a.dateModified).getTime() : 0;
      const bTime = b.dateModified ? new Date(b.dateModified).getTime() : 0;
      return bTime - aTime;
    })[0];
  }, [titlePageSegments]);

  const demandPackageDocumentsFromMatter = useMemo(
    () =>
      segmentsToDocuments(
        existingDemandPackageSegments.filter(
          (segment) => Number(segment.order ?? 0) !== TITLE_PAGE_SEGMENT_ORDER,
        ),
      ),
    [existingDemandPackageSegments],
  );

  const titlePageDocument = useMemo(() => {
    if (!titlePageSegment?.id) return null;
    return segmentsToDocuments([titlePageSegment])[0] ?? null;
  }, [titlePageSegment]);

  const settlementDemandFormSchema =
    createSettlementDemandFormSchema(negotiationType);
  const defaultFormValues = createDefaultFormValues(negotiationType);
  const initialCoverageType = coverageType ?? (isMedpay ? "MedPay" : undefined);
  const isInitialMedPay = initialCoverageType === "MedPay";

  const baseFormValues =
    editing && normalizedSettlementInitialValues
      ? {
          ...defaultFormValues,
          ...normalizedSettlementInitialValues,
          coverageType:
            normalizedSettlementInitialValues.coverageType ??
            initialCoverageType,
        }
      : {
          ...defaultFormValues,
          insuranceId: selectedInsurance?.id ?? undefined,
          ...(negotiationType === "Demand"
            ? {
                attorneyFeePercentage: matter.attorneyFee ?? null,
                coverageType: initialCoverageType,
              }
            : {
                attorneyFeePercentage: matter.attorneyFee ?? null,
                coverageType: initialCoverageType,
                medPay: isInitialMedPay,
              }),
        };

  const settlementDemandForm = useForm<
    z.infer<typeof settlementDemandFormSchema>
  >({
    resolver: zodResolver(settlementDemandFormSchema),
    values: {
      ...baseFormValues,
      settlementDemandPackageSegments:
        negotiationType === "Demand"
          ? documentsToSegments(demandPackageDocumentsFromMatter)
          : [],
    } as z.infer<typeof settlementDemandFormSchema>,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });
  const generatePackageForm = useForm<
    z.infer<typeof generatePackageFormSchema>
  >({
    resolver: zodResolver(generatePackageFormSchema),
    defaultValues: {
      packageName: `Demand Package - ${dayjs().format("MMM D YYYY")}`,
    },
    mode: "onSubmit",
  });

  const [isSelectExhibitSlideoverOpen, setIsSelectExhibitSlideoverOpen] =
    useState(false);
  const [isSelectTitlePageSlideoverOpen, setIsSelectTitlePageSlideoverOpen] =
    useState(false);
  const [
    isSelectFirmFileExhibitSlideoverOpen,
    setIsSelectFirmFileExhibitSlideoverOpen,
  ] = useState(false);
  const [
    isSelectFirmFileTitlePageSlideoverOpen,
    setIsSelectFirmFileTitlePageSlideoverOpen,
  ] = useState(false);
  const [isSyncingExhibits, setIsSyncingExhibits] = useState(false);
  const [isSyncingTitlePage, setIsSyncingTitlePage] = useState(false);
  const [isGeneratePackageSlideoverOpen, setIsGeneratePackageSlideoverOpen] =
    useState(false);
  const [exhibitPendingDelete, setExhibitPendingDelete] = useState<{
    segmentId: string;
    name?: string;
    type: "exhibit" | "title";
  } | null>(null);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [reorderedExhibits, setReorderedExhibits] = useState<
    DemandPackageExhibitFormValue[]
  >([]);
  const [isSavingReorder, setIsSavingReorder] = useState(false);
  const reorderSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );
  const isGeneratingDemandPackage =
    mergeMatterFiles.isPending || createDemandFileReference.isPending;

  const demandPackageExhibits = useMemo(
    () =>
      negotiationType === "Demand" ? demandPackageDocumentsFromMatter : [],
    [demandPackageDocumentsFromMatter, negotiationType],
  );
  const currentDemandPackageSnapshot = useMemo(
    () =>
      buildDemandPackageSnapshot({
        titlePageFileId: titlePageDocument?.fileId,
        exhibits: demandPackageExhibits,
      }),
    [demandPackageExhibits, titlePageDocument?.fileId],
  );
  const [savedDemandPackageSnapshot, setSavedDemandPackageSnapshot] = useState(
    currentDemandPackageSnapshot,
  );
  const previousOpenRef = useRef(open);
  const previousDemandIdRef = useRef(selectedDemandId);
  const titlePageFile = useMemo(() => {
    if (!titlePageDocument?.fileId) return undefined;
    return matter.files.find(
      (matterFile) => matterFile.id === titlePageDocument.fileId,
    );
  }, [matter.files, titlePageDocument]);

  useEffect(() => {
    const opened = open && !previousOpenRef.current;
    const demandChanged = previousDemandIdRef.current !== selectedDemandId;

    if (opened || (open && demandChanged)) {
      setSavedDemandPackageSnapshot(currentDemandPackageSnapshot);
    }

    previousOpenRef.current = open;
    previousDemandIdRef.current = selectedDemandId;
  }, [currentDemandPackageSnapshot, open, selectedDemandId]);

  useEffect(() => {
    if (!isReorderMode) {
      setReorderedExhibits(demandPackageExhibits);
    }
  }, [demandPackageExhibits, isReorderMode]);

  const displayedExhibits = isReorderMode
    ? reorderedExhibits
    : demandPackageExhibits;

  const demandPackageExhibitIds = useMemo(
    () => displayedExhibits.map((exhibit) => exhibit.clientId),
    [displayedExhibits],
  );

  const hasReorderChanges = useMemo(() => {
    if (!isReorderMode) return false;
    if (reorderedExhibits.length !== demandPackageExhibits.length) {
      return true;
    }
    return reorderedExhibits.some((exhibit, index) => {
      return (
        exhibit.segmentId !== demandPackageExhibits[index]?.segmentId ||
        exhibit.clientId !== demandPackageExhibits[index]?.clientId
      );
    });
  }, [demandPackageExhibits, isReorderMode, reorderedExhibits]);

  const isSyncingPackageSegments = isSyncingExhibits || isSyncingTitlePage;
  const isReorderBusy = isSyncingExhibits || isSavingReorder;

  const persistNewDocuments = async (
    files: MatterFile[],
    options?: { isTitlePage?: boolean },
  ) => {
    if (!canCreateDemandPackageSegments) {
      toast.error(
        "You do not have permission to add demand package documents.",
      );
      throw new Error("missing-create-segment-permission");
    }
    if (negotiationType !== "Demand") return;
    if (!selectedDemandId) {
      toast.error("Save the settlement demand before adding documents.");
      throw new Error("no-demand");
    }
    const isTitlePage = options?.isTitlePage ?? false;
    const selectedFileIds = new Set(
      [
        ...demandPackageExhibits.map((exhibit) => exhibit.fileId),
        titlePageDocument?.fileId,
      ].filter(Boolean) as string[],
    );

    if (isTitlePage) {
      if (titlePageDocument && !canDeleteDemandPackageSegments) {
        toast.error("You do not have permission to replace the title page.");
        throw new Error("missing-delete-segment-permission");
      }
      if (files.length === 0) {
        toast.error("Select a document for the title page.");
        throw new Error("no-title-page");
      }
      const [file] = files;
      if (files.length > 1) {
        toast.error("Select only one document for the title page.");
        throw new Error("multiple-title-page");
      }
      if (titlePageDocument?.fileId === file.id) {
        toast.error("This document is already set as the title page.");
        throw new Error("existing-title-page");
      }
      if (demandPackageExhibits.some((exhibit) => exhibit.fileId === file.id)) {
        toast.error("This document is already selected as an exhibit.");
        throw new Error("duplicate-title-page");
      }

      setIsSyncingTitlePage(true);
      try {
        const titlePageSegmentIds = titlePageSegments
          .map((segment) => segment.id)
          .filter(Boolean) as string[];
        if (titlePageSegmentIds.length > 0) {
          await Promise.all(
            titlePageSegmentIds.map((segmentId) =>
              deleteMatterSettlementDemandPackageSegmentV1({
                matterId: matter.id,
                id: segmentId,
              }),
            ),
          );
        }
        await createMatterSettlementDemandPackageSegmentV1({
          matterId: matter.id,
          settlementDemandId: selectedDemandId,
          fileId: file.id,
          order: TITLE_PAGE_SEGMENT_ORDER,
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.matters, matter.id],
        });
      } finally {
        setIsSyncingTitlePage(false);
      }
      return;
    }

    const newFiles = files.filter((file) => !selectedFileIds.has(file.id));
    if (newFiles.length === 0) {
      toast.error("These documents are already selected");
      throw new Error("no-new-documents");
    }

    setIsSyncingExhibits(true);
    try {
      await Promise.all(
        newFiles.map((file) =>
          createMatterSettlementDemandPackageSegmentV1({
            matterId: matter.id,
            settlementDemandId: selectedDemandId,
            fileId: file.id,
          }),
        ),
      );
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, matter.id],
      });
    } finally {
      setIsSyncingExhibits(false);
    }
  };

  const handleDocumentSelection = async (files: MatterFile[]) => {
    await persistNewDocuments(files);
    setIsSelectExhibitSlideoverOpen(false);
    toast.success("Document added to demand package.");
  };

  const handleTitlePageSelection = async (files: MatterFile[]) => {
    await persistNewDocuments(files, { isTitlePage: true });
    setIsSelectTitlePageSlideoverOpen(false);
    toast.success("Title page updated.");
  };

  const handleFirmFileSelection = async (
    files: FirmFile[],
    options?: { isTitlePage?: boolean },
  ) => {
    const handledErrorMessages = new Set([
      "handled",
      "no-demand",
      "no-title-page",
      "multiple-title-page",
      "existing-title-page",
      "existing-documents",
      "no-new-documents",
    ]);

    if (negotiationType !== "Demand") return;
    if (!selectedDemandId) {
      toast.error("Save the settlement demand before adding documents.");
      throw new Error("handled");
    }

    const isTitlePage = options?.isTitlePage ?? false;
    if (isTitlePage && files.length === 0) {
      toast.error("Select a document for the title page.");
      throw new Error("handled");
    }
    if (isTitlePage && files.length > 1) {
      toast.error("Select only one document for the title page.");
      throw new Error("handled");
    }

    const setSyncing = isTitlePage
      ? setIsSyncingTitlePage
      : setIsSyncingExhibits;
    setSyncing(true);

    const existingNames = new Set(
      matter.files.map((file) => file.name.toLowerCase()),
    );
    const usedNames = new Set(existingNames);
    const createdFiles: MatterFile[] = [];
    const failedFiles: string[] = [];

    const getFirmFileName = (firmFile: FirmFile) => {
      const baseName = firmFile.name.trim() || "firm-file";
      const hasExtension = Boolean(getFileNameParts(baseName).fileExtension);
      if (hasExtension) return baseName;
      if (!firmFile.presignedUrlKey) {
        return baseName;
      }
      const keyName = firmFile.presignedUrlKey.split("/").pop();
      const keyExtension = keyName
        ? getFileNameParts(keyName).fileExtension
        : "";
      return keyExtension ? `${baseName}.${keyExtension}` : baseName;
    };

    const buildUniqueName = (fileName: string) => {
      if (!usedNames.has(fileName.toLowerCase())) {
        usedNames.add(fileName.toLowerCase());
        return fileName;
      }
      const { fileName: baseName, fileExtension } = getFileNameParts(fileName);
      let index = 1;
      let candidate = `${baseName} (${index})${fileExtension ? `.${fileExtension}` : ""}`;
      while (usedNames.has(candidate.toLowerCase())) {
        index += 1;
        candidate = `${baseName} (${index})${fileExtension ? `.${fileExtension}` : ""}`;
      }
      usedNames.add(candidate.toLowerCase());
      return candidate;
    };

    try {
      for (const firmFile of files) {
        try {
          if (!firmFile.presignedUrlKey) {
            failedFiles.push(firmFile.name);
            continue;
          }
          const presigned = await generateS3ItemPreSignedUrlV1("firm-file")({
            clientMethod: "get_object",
            key: firmFile.presignedUrlKey,
          });
          const response = await fetch(presigned.data.presignedUrl, {
            method: "GET",
          });
          if (!response.ok) {
            failedFiles.push(firmFile.name);
            continue;
          }
          const blob = await response.blob();
          const contentType =
            response.headers.get("content-type") || blob.type || undefined;
          const fileName = buildUniqueName(getFirmFileName(firmFile));
          const file = new File([blob], fileName, {
            type: contentType,
          });
          const created = await createMatterFile.mutateAsync({
            matterId: matter.id,
            isFolder: false,
            idLocation: "",
            name: fileName,
            file,
          });
          createdFiles.push(created.data);
        } catch (error) {
          console.error("Failed to import firm file", error);
          failedFiles.push(firmFile.name);
        }
      }

      if (createdFiles.length === 0) {
        toast.error("Unable to add firm files. Please try again.");
        throw new Error("handled");
      }

      await persistNewDocuments(createdFiles, { isTitlePage });
      if (isTitlePage) {
        setIsSelectFirmFileTitlePageSlideoverOpen(false);
        toast.success("Title page updated.");
      } else {
        setIsSelectFirmFileExhibitSlideoverOpen(false);
        toast.success(
          createdFiles.length === 1
            ? "Firm file added to demand package."
            : "Firm files added to demand package.",
        );
      }
      if (failedFiles.length > 0) {
        toast.error(
          `Unable to add ${failedFiles.length} firm file${failedFiles.length === 1 ? "" : "s"}.`,
        );
      }
    } catch (error) {
      if (error instanceof Error && handledErrorMessages.has(error.message)) {
        throw error;
      }
      console.error("Failed to add firm files", error);
      toast.error("Unable to add firm files. Please try again.");
      throw error;
    } finally {
      setSyncing(false);
    }
  };

  const handleGenerateDocument = () => {
    openGenerateDocumentModal({
      matter,
      contacts,
      afterGenerateFn: (files) => {
        void handleDocumentSelection(files);
      },
      hideSaveAndMerge: true,
    });
  };

  const openGenerateDemandPackageSlideover = () => {
    if (negotiationType !== "Demand") return;
    if (!selectedDemandId) {
      toast.error("Save the settlement demand before generating a package.");
      return;
    }
    if (demandPackageExhibits.length === 0) {
      toast.error("Add exhibits before generating a demand package.");
      return;
    }
    generatePackageForm.reset({
      packageName: `Demand Package - ${dayjs().format("MMM D YYYY")}`,
    });
    setIsGeneratePackageSlideoverOpen(true);
  };

  const handleGeneratePackageSubmit = async (
    values: z.infer<typeof generatePackageFormSchema>,
  ) => {
    await handleGenerateDemandPackage(values.packageName);
  };

  const handleGenerateDemandPackage = async (packageName: string) => {
    if (negotiationType !== "Demand") return;
    if (!selectedDemandId) {
      toast.error("Save the settlement demand before generating a package.");
      return;
    }

    const fileIds = [
      titlePageDocument?.fileId,
      ...demandPackageExhibits.map((exhibit) => exhibit.fileId),
    ].filter((fileId): fileId is string => Boolean(fileId));

    if (fileIds.length === 0) {
      toast.error("Add exhibits before generating a demand package.");
      return;
    }

    const trimmedName = packageName.trim();
    if (!trimmedName) {
      toast.error("Enter a name for the demand package.");
      return;
    }
    const normalizedName = trimmedName.replace(/\.pdf$/i, "");

    try {
      const mergedFileResponse = await mergeMatterFiles.mutateAsync({
        matterId: matter.id,
        files: fileIds,
        name: `${normalizedName}.pdf`,
      });

      const mergedFileId = mergedFileResponse.data.id;
      if (!mergedFileId) {
        toast.error("Unable to generate demand package. Please try again.");
        return;
      }

      await createDemandFileReference.mutateAsync({
        matterId: matter.id,
        fileId: mergedFileId,
        settlementDemandId: selectedDemandId,
      });
      setIsGeneratePackageSlideoverOpen(false);
      generatePackageForm.reset({
        packageName: `Demand Package - ${dayjs().format("MMM D YYYY")}`,
      });
    } catch {
      // Errors handled via mutation hooks
    }
  };

  const isGeneratePackageDirty = generatePackageForm.formState.isDirty;
  const hasFormDirtyFields = isAnyDirtyInFieldTree(
    settlementDemandForm.formState.dirtyFields,
  );
  const isSettlementFormDirty =
    settlementDemandForm.formState.isDirty || hasFormDirtyFields;
  const isDemandPackageDirty =
    negotiationType === "Demand" &&
    editing &&
    currentDemandPackageSnapshot !== savedDemandPackageSnapshot;
  const isSettlementDirty = isSettlementFormDirty || isDemandPackageDirty;
  const canSubmitSettlement = Boolean(initialData) || isSettlementDirty;

  const handleRemoveDocument = (
    segmentId: string,
    options?: { isTitlePage?: boolean },
  ) => {
    if (!canDeleteDemandPackageSegments) return;
    if (!selectedDemandId) {
      toast.error("Save the settlement demand before removing documents.");
      return;
    }

    const isTitlePage = options?.isTitlePage ?? false;
    const exhibit = isTitlePage
      ? titlePageDocument?.segmentId === segmentId
        ? { segmentId }
        : undefined
      : demandPackageExhibits.find(
          (document) => document.segmentId === segmentId,
        );

    if (!exhibit?.segmentId) {
      toast.error("Unable to find the selected document.");
      return;
    }

    if (isTitlePage) {
      setIsSyncingTitlePage(true);
    } else {
      setIsSyncingExhibits(true);
    }
    void deleteMatterSettlementDemandPackageSegmentV1({
      matterId: matter.id,
      id: exhibit.segmentId,
    })
      .then(async () => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.matters, matter.id],
        });
        toast.success(
          isTitlePage
            ? "Title page removed from demand package."
            : "Document removed from demand package.",
        );
      })
      .catch(() => {
        toast.error("Unable to delete document. Please try again.");
      })
      .finally(() => {
        if (isTitlePage) {
          setIsSyncingTitlePage(false);
        } else {
          setIsSyncingExhibits(false);
        }
      });
  };

  const handleReorderDragEnd = (event: DragEndEvent) => {
    if (!isReorderMode || isSyncingPackageSegments || isSavingReorder) {
      return;
    }
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    setReorderedExhibits((items) => {
      const oldIndex = items.findIndex((item) => item.clientId === active.id);
      const newIndex = items.findIndex((item) => item.clientId === over.id);
      if (oldIndex === -1 || newIndex === -1) {
        return items;
      }
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const handleSaveReorderedExhibits = async () => {
    if (!canUpdateDemandPackageSegments) return;
    if (!selectedDemandId) {
      toast.error("Save the settlement demand before reordering items.");
      return;
    }
    if (!hasReorderChanges) {
      setIsReorderMode(false);
      return;
    }

    const segmentsPayload = reorderedExhibits.map((exhibit, index) => {
      if (!exhibit.segmentId) {
        return null;
      }
      const segment = existingSegmentsById.get(exhibit.segmentId);
      if (!segment || !segment.dateModified || !segment.id) {
        return null;
      }
      return {
        id: segment.id,
        order:
          TITLE_PAGE_SEGMENT_ORDER +
          (index + 1) * DEMAND_SEGMENT_ORDER_INCREMENT,
        dateModified: segment.dateModified,
      };
    });

    const filteredSegments = segmentsPayload.filter(
      (
        payload,
      ): payload is {
        id: string;
        order: number;
        dateModified: string;
      } => Boolean(payload),
    );

    if (filteredSegments.length !== reorderedExhibits.length) {
      toast.error(
        "Unable to save the new order. Please refresh and try again.",
      );
      return;
    }

    setIsSavingReorder(true);
    setIsSyncingExhibits(true);
    try {
      await updateMatterSettlementDemandPackageSegmentV1({
        matterId: matter.id,
        settlementDemandPackageSegments: filteredSegments,
      });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, matter.id],
      });
      toast.success("Demand package order updated.");
      setIsReorderMode(false);
    } catch {
      toast.error("Unable to update document order. Please try again.");
    } finally {
      setIsSavingReorder(false);
      setIsSyncingExhibits(false);
    }
  };

  const handleStartReorder = () => {
    if (demandPackageExhibits.length <= 1) {
      return;
    }
    setReorderedExhibits(demandPackageExhibits);
    setIsReorderMode(true);
  };

  const handleCancelReorder = () => {
    setIsReorderMode(false);
    setReorderedExhibits(demandPackageExhibits);
  };

  // Use appropriate hooks based on negotiation type
  const createSettlementDemand = useCreateMatterSettlementDemand();
  const updateSettlementDemand = useUpdateMatterSettlementDemand();
  const deleteSettlementDemand = useDeleteMatterSettlementDemand();

  const createSettlementOffer = useCreateMatterSettlementOffer();
  const updateSettlementOffer = useUpdateMatterSettlementOffer();
  const deleteSettlementOffer = useDeleteMatterSettlementOffer();

  const isFormMutationLoading =
    negotiationType === "Demand"
      ? createSettlementDemand.isPending ||
        updateSettlementDemand.isPending ||
        deleteSettlementDemand.isPending
      : createSettlementOffer.isPending ||
        updateSettlementOffer.isPending ||
        deleteSettlementOffer.isPending;

  const isFormLoading = isFormMutationLoading;

  const insuranceIdFormValue = settlementDemandForm.watch("insuranceId");
  const selectedInsuranceId = insuranceIdFormValue ?? selectedInsurance?.id;
  const selectedInsuranceRecord =
    selectedInsuranceId !== undefined
      ? matter.insurances.find(
          (insurance) => insurance.id === selectedInsuranceId,
        )
      : undefined;
  const insuredConnection = getInsuranceContactConnection(
    selectedInsuranceRecord,
    "Insured",
  );
  const clientConnection = getMatterContactConnections(matter).find(
    (connection) => connection.label === "Client",
  );
  const isFirstPartyInsurance =
    !!insuredConnection?.targetObjectId &&
    insuredConnection.targetObjectId === clientConnection?.contactId;
  const isAutoInsurance = selectedInsuranceRecord?.type === "Auto";
  const isCoverageTypeEditable = !editing && !selectedInsurance;
  const shouldShowDemandCoverageType =
    negotiationType === "Demand" && isAutoInsurance;
  const shouldShowOfferCoverageType =
    negotiationType === "Offer" && isAutoInsurance && isFirstPartyInsurance;
  const offerCoverageTypeFormValue =
    negotiationType === "Offer"
      ? settlementDemandForm.watch("coverageType")
      : undefined;
  const isOfferMedPay =
    negotiationType === "Offer"
      ? offerCoverageTypeFormValue === "MedPay"
      : false;
  const resolveOfferCoverageKey = (
    coverageType?: string | null,
    isMedPay?: boolean | null,
  ) => coverageType ?? (isMedPay ? "MedPay" : "NonMedPay");
  const selectedOfferCoverageKey =
    negotiationType === "Offer"
      ? resolveOfferCoverageKey(offerCoverageTypeFormValue, isOfferMedPay)
      : undefined;
  const matterInsuranceIsMedpayFormValue =
    negotiationType === "Offer"
      ? shouldShowOfferCoverageType
        ? isOfferMedPay
        : false
      : false;
  const documentCount = demandPackageExhibits.length;
  const allowedExhibitFileTypes = ["pdf", "jpg", "jpeg", "png"];
  const isGenerateDocumentLoading = contactIds.length > 0 && isContactsLoading;
  const pendingDeleteName = exhibitPendingDelete?.name ?? "this document";

  const isAnotherSettlementAccepted =
    negotiationType === "Offer"
      ? matter.settlementOffers.some(
          (offer) =>
            offer.accepted && // Only check accepted
            resolveOfferCoverageKey(offer.coverageType, offer.medPay) ===
              selectedOfferCoverageKey && // check if settlement is same coverage type
            (editing
              ? offer.id ===
                  (selectedSettlement && isOfferSettlement(selectedSettlement)
                    ? selectedSettlement.id
                    : undefined) && // If editing, check if each settlement is for same insurance as offer
                offer.id !==
                  (selectedSettlement && isOfferSettlement(selectedSettlement)
                    ? selectedSettlement.id
                    : undefined) // If editing, check if each settlement is not the same as the selected settlement
              : offer.insuranceId === insuranceIdFormValue), // If creating, check if each settlement is for the same insurance as the form value
        )
      : false;
  const closeAfterSaveRef = useRef(false);

  const onSubmit = async (data: z.infer<typeof settlementDemandFormSchema>) => {
    const closeAfterSave = closeAfterSaveRef.current;
    const { settlementDemandPackageSegments: _unusedSegments, ...formData } =
      data;
    void _unusedSegments;
    let resData;

    if (negotiationType === "Demand") {
      const demandFormData = formData as z.infer<
        typeof MatterSettlementDemandSchema
      >;
      const coverageType =
        demandFormData.coverageType ??
        (editing && selectedSettlement && isDemandSettlement(selectedSettlement)
          ? selectedSettlement.coverageType
          : initialCoverageType);
      if (shouldShowDemandCoverageType && !coverageType) {
        settlementDemandForm.setError("coverageType", {
          type: "manual",
          message: "Coverage type is required for auto settlements.",
        });
        return;
      }
      const {
        attorneyFeeAmount: _attorneyFeeAmount,
        accepted,
        acceptedDate,
        acceptedWithIssues: _acceptedWithIssues,
        medPayRefund,
        medPayRefundAmount,
        ...demandPayloadBase
      } = demandFormData;
      void _attorneyFeeAmount;
      void _acceptedWithIssues;
      if (medPayRefund && medPayRefundAmount == null) {
        settlementDemandForm.setError("medPayRefundAmount", {
          type: "manual",
          message: "MedPay refund amount is required when refund is enabled.",
        });
        return;
      }
      if (accepted && !acceptedDate) {
        settlementDemandForm.setError("acceptedDate", {
          type: "manual",
          message: "Accepted date is required when a demand is accepted.",
        });
        return;
      }
      if (isAutoInsurance && isFirstPartyInsurance) {
        demandPayloadBase.policyLimit = false;
      }
      const isDemandMedPay = demandFormData.coverageType === "MedPay";
      const demandPayload = omitBy(
        {
          ...demandPayloadBase,
          coverageType,
          ...(isDemandMedPay
            ? {
                accepted,
                ...(accepted && acceptedDate ? { acceptedDate } : {}),
              }
            : {}),
          ...(medPayRefund
            ? {
                medPayRefund,
                medPayRefundAmount,
              }
            : {}),
        },
        isNil,
      );
      if (
        editing &&
        selectedSettlement &&
        isDemandSettlement(selectedSettlement)
      ) {
        const res = await updateSettlementDemand.mutateAsync({
          ...demandPayload,
          matterId: matter.id,
          dateModified: selectedSettlement.dateModified,
          id: selectedSettlement.id,
        });
        resData = res;
      } else {
        const res = await createSettlementDemand.mutateAsync({
          ...demandPayload,
          matterId: matter.id,
        });
        resData = res;
        updateSlideoverPayload("settlement-demand", {
          title: SLIDEOVER_TITLE.SETTLEMENT_DEMAND.UPDATE,
          description: SLIDEOVER_DESCRIPTION.SETTLEMENT_DEMAND.UPDATE,
          editing: true,
          settlementId: resData.data.id,
        });
      }
    } else {
      // Type guard to ensure we have offer data
      if ("accepted" in data) {
        const offerFormData = formData as z.infer<
          typeof MatterSettlementOfferSchema
        > & { issues?: boolean };
        const offerCoverageType = offerFormData.coverageType;
        if (shouldShowOfferCoverageType && !offerCoverageType) {
          settlementDemandForm.setError("coverageType", {
            type: "manual",
            message:
              "Coverage type is required for first-party auto settlements.",
          });
          return;
        }
        const {
          accepted,
          acceptedDate,
          attorneyFeeAmount: _attorneyFeeAmount,
          acceptedWithIssues: _acceptedWithIssues,
          issues,
          medPayRefund,
          medPayRefundAmount,
          status,
          ...offerPayloadBase
        } = offerFormData;
        void _attorneyFeeAmount;
        void _acceptedWithIssues;
        const isOfferMedPayCoverage =
          shouldShowOfferCoverageType && offerCoverageType === "MedPay";
        if (medPayRefund && medPayRefundAmount == null) {
          settlementDemandForm.setError("medPayRefundAmount", {
            type: "manual",
            message: "MedPay refund amount is required when refund is enabled.",
          });
          return;
        }
        if (accepted && !acceptedDate) {
          settlementDemandForm.setError("acceptedDate", {
            type: "manual",
            message: "Accepted date is required when an offer is accepted.",
          });
          return;
        }
        if (accepted && !status) {
          settlementDemandForm.setError("status", {
            type: "manual",
            message: "Money status is required when an offer is accepted.",
          });
          return;
        }
        const offerPayload = omitBy(
          {
            ...offerPayloadBase,
            coverageType: shouldShowOfferCoverageType
              ? offerCoverageType
              : undefined,
            medPay: isOfferMedPayCoverage,
            accepted,
            acceptedWithIssues:
              accepted && !isOfferMedPayCoverage && issues ? issues : false,
            ...(accepted && acceptedDate ? { acceptedDate } : {}),
            ...(accepted && status ? { status } : {}),
            ...(isOfferMedPayCoverage && medPayRefund
              ? {
                  medPayRefund,
                  medPayRefundAmount,
                }
              : {}),
          },
          isNil,
        );
        if (
          editing &&
          selectedSettlement &&
          isOfferSettlement(selectedSettlement)
        ) {
          const res = await updateSettlementOffer.mutateAsync({
            ...offerPayload,
            matterId: matter.id,
            dateModified: selectedSettlement.dateModified,
            id: selectedSettlement.id,
          });
          resData = res;
        } else {
          const res = await createSettlementOffer.mutateAsync({
            ...offerPayload,
            matterId: matter.id,
          });
          resData = res;
          updateSlideoverPayload("settlement-demand", {
            title: SLIDEOVER_TITLE.SETTLEMENT_OFFER.UPDATE,
            description: SLIDEOVER_DESCRIPTION.SETTLEMENT_OFFER.UPDATE,
            editing: true,
            settlementId: resData.data.id,
          });
        }
      }
    }

    if (resData) {
      const settlement = omitBy(resData.data, isNil);
      settlementDemandForm.reset({
        ...defaultFormValues,
        ...settlement,
        settlementDemandPackageSegments:
          negotiationType === "Demand"
            ? documentsToSegments(demandPackageExhibits)
            : [],
      });
      setSavedDemandPackageSnapshot(currentDemandPackageSnapshot);
      if (closeAfterSave) {
        setOpen(false);
      }
    }
  };

  const handleDelete = () => {
    if (!selectedSettlement) return;

    if (
      negotiationType === "Demand" &&
      isDemandSettlement(selectedSettlement)
    ) {
      deleteSettlementDemand.mutate({
        matterId: matter.id,
        id: selectedSettlement.id,
      });
    } else if (
      negotiationType === "Offer" &&
      isOfferSettlement(selectedSettlement)
    ) {
      deleteSettlementOffer.mutate({
        matterId: matter.id,
        id: selectedSettlement.id,
      });
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      settlementDemandForm.reset();
      setIsSelectExhibitSlideoverOpen(false);
      setIsSelectTitlePageSlideoverOpen(false);
    }
  }, [open, settlementDemandForm]);

  const matterSettlementAmountFormValue = settlementDemandForm.watch("amount");

  const matterSettlementAcceptedFormValue =
    negotiationType === "Offer"
      ? settlementDemandForm.watch("accepted")
      : false;
  const matterSettlementDemandAcceptedFormValue =
    negotiationType === "Demand"
      ? settlementDemandForm.watch("accepted")
      : false;
  const demandCoverageTypeFormValue =
    negotiationType === "Demand"
      ? settlementDemandForm.watch("coverageType")
      : undefined;
  const matterSettlementMedPayFormValue =
    negotiationType === "Demand"
      ? demandCoverageTypeFormValue === "MedPay"
      : shouldShowOfferCoverageType
        ? isOfferMedPay
        : false;
  const shouldShowDemandAcceptance =
    negotiationType === "Demand" && matterSettlementMedPayFormValue;
  const shouldShowPolicyLimits =
    negotiationType === "Demand"
      ? !(isAutoInsurance && isFirstPartyInsurance)
      : !matterSettlementMedPayFormValue;
  const matterSettlementMedPayRefundFormValue =
    settlementDemandForm.watch("medPayRefund");
  const settlementModalPreview = selectedInsuranceRecord ? (
    <SelectedInsuranceCard
      insurance={selectedInsuranceRecord}
      showMedpayBadge={
        negotiationType === "Offer" && Boolean(matterInsuranceIsMedpayFormValue)
      }
      isFirstPartyInsurance={isFirstPartyInsurance}
    />
  ) : (
    <Typography size="sm" className="text-muted-foreground">
      Select insurance to view coverage details.
    </Typography>
  );

  return (
    <>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        title={title}
        description={description}
        size="4xl"
        loading={isFormLoading}
        isDirty={canEditSettlement && isSettlementDirty}
        preview={settlementModalPreview}
        previewLabel={null}
        footer={
          <div className="flex w-full items-center gap-2">
            {editing && selectedSettlement ? (
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  setIsDeleting(true);
                }}
              >
                Delete
              </Button>
            ) : (
              <div className="hidden sm:block sm:flex-1" />
            )}
            <div className="flex grow justify-end gap-x-2">
              <FormCancelButton setOpen={setOpen} isDirty={isSettlementDirty} />
              <FormSubmitButton
                submitFormId={settlementDemandFormId}
                variant="light-primary"
                loading={isFormLoading}
                disabled={!canSubmitSettlement}
                overrideDisabled
                submitFn={() => {
                  closeAfterSaveRef.current = false;
                }}
              />
              <FormSubmitButton
                submitFormId={settlementDemandFormId}
                variant="default"
                loading={isFormLoading}
                disabled={!canSubmitSettlement}
                overrideDisabled
                submitFn={() => {
                  closeAfterSaveRef.current = true;
                }}
                title="Save and close"
              />
            </div>
          </div>
        }
      >
        <Form {...settlementDemandForm}>
          <form
            id={settlementDemandFormId}
            className="relative flex grow flex-col overflow-hidden"
            onSubmit={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await settlementDemandForm.handleSubmit(onSubmit)();
            }}
          >
            {isFormLoading && <FormLoadingOverlay />}
            <div className="overflow-y-auto">
              <fieldset
                className={cn(
                  slideoverFieldsetClassName,
                  "grid-cols-1 md:grid-cols-2",
                )}
                disabled={
                  editing
                    ? !hasActionPermission(
                        settlementPermissionKey,
                        ACTION_PERMISSION_VALUES.update,
                      )
                    : false
                }
              >
                <div className="col-span-full grid grid-cols-1 gap-4 md:grid-cols-2">
                  <SelectMatterInsuranceFormField
                    matter={matter}
                    label="Insurance*"
                    register={settlementDemandForm.register("insuranceId")}
                    disabled={!!selectedInsurance || editing}
                    placeholder="Select insurance"
                  />
                  {negotiationType === "Demand" ? (
                    shouldShowDemandCoverageType ? (
                      <SimpleSelectFormField
                        label="Coverage type*"
                        placeholder="Select coverage type"
                        register={settlementDemandForm.register("coverageType")}
                        items={SETTLEMENT_COVERAGE_TYPES}
                        formatItemLabel={(item) => item}
                        disabled={!isCoverageTypeEditable}
                      />
                    ) : (
                      <div />
                    )
                  ) : (
                    <>
                      {shouldShowOfferCoverageType ? (
                        <SimpleSelectFormField
                          label="Coverage type*"
                          placeholder="Select coverage type"
                          register={settlementDemandForm.register(
                            "coverageType",
                          )}
                          items={SETTLEMENT_COVERAGE_TYPES}
                          formatItemLabel={(item) => item}
                          disabled={!isCoverageTypeEditable}
                        />
                      ) : (
                        <div />
                      )}
                    </>
                  )}
                </div>
                <div className="col-span-full grid h-[62px] grid-cols-subgrid items-center">
                  {negotiationType === "Demand" ? (
                    <>
                      <CurrencyFormField
                        register={settlementDemandForm.register("amount")}
                        label="Demand amount"
                        disabled={!insuranceIdFormValue}
                        onChange={(value) => {
                          const amount = Number(value);
                          if (amount) {
                            settlementDemandForm.setValue(
                              "attorneyFeeAmount",
                              calculateAttorneyFee(
                                amount,
                                settlementDemandForm.watch(
                                  "attorneyFeePercentage",
                                ),
                              ),
                            );
                          }
                        }}
                      />
                      <div>
                        {shouldShowPolicyLimits && (
                          <CheckboxFormField
                            register={settlementDemandForm.register(
                              "policyLimit",
                            )}
                            label="Policy limits"
                            disabled={!insuranceIdFormValue}
                            disabledMessage="Please select an insurance first"
                          />
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      {shouldShowPolicyLimits && (
                        <div>
                          <CheckboxFormField
                            register={settlementDemandForm.register(
                              "policyLimit",
                            )}
                            label="Policy limits"
                            disabled={!insuranceIdFormValue}
                            disabledMessage="Please select an insurance first"
                          />
                        </div>
                      )}
                      <CurrencyFormField
                        register={settlementDemandForm.register("amount")}
                        label="Offer amount"
                        disabled={!insuranceIdFormValue}
                        onChange={(value) => {
                          const amount = Number(value);
                          if (amount) {
                            settlementDemandForm.setValue(
                              "attorneyFeeAmount",
                              calculateAttorneyFee(
                                amount,
                                settlementDemandForm.watch(
                                  "attorneyFeePercentage",
                                ),
                              ),
                            );
                          }
                        }}
                      />
                    </>
                  )}
                </div>
                {matterSettlementMedPayFormValue && (
                  <>
                    <SwitchFormField
                      register={settlementDemandForm.register(
                        negotiationType === "Demand"
                          ? "medPayRefund"
                          : "medPayRefund",
                      )}
                      label={<span>MedPay refund</span>}
                    />
                    <div />
                    {matterSettlementMedPayRefundFormValue && (
                      <>
                        <CurrencyFormField
                          register={settlementDemandForm.register(
                            negotiationType === "Demand"
                              ? "medPayRefundAmount"
                              : "medPayRefundAmount",
                          )}
                          label="MedPay refund amount"
                        />
                        <div />
                      </>
                    )}
                  </>
                )}
                <DateFormField
                  register={settlementDemandForm.register(
                    negotiationType === "Demand" ? "dateSent" : "dateReceived",
                  )}
                  name={
                    negotiationType === "Demand" ? "dateSent" : "dateReceived"
                  }
                  label={
                    negotiationType === "Demand"
                      ? "Date Sent*"
                      : "Date Received*"
                  }
                />
                {negotiationType === "Demand" &&
                  (() => {
                    const dateSentFormValue = settlementDemandForm.watch(
                      "dateSent",
                    ) as string | undefined;
                    return (
                      <DateFormField
                        register={settlementDemandForm.register("dateExpires")}
                        name="dateExpires"
                        label="Date expires*"
                        suggestedDate={
                          dateSentFormValue
                            ? dayjs(dateSentFormValue).add(30, "day").toDate()
                            : undefined
                        }
                        suggestedDateText={
                          dateSentFormValue
                            ? `${formatDate(
                                dayjs(dateSentFormValue)
                                  .add(30, "day")
                                  .toDate(),
                              )} is 30 days from the date sent.`
                            : undefined
                        }
                      />
                    );
                  })()}
                {shouldShowDemandAcceptance && (
                  <div className="border-muted bg-muted/40 col-span-full rounded-md border p-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <SwitchFormField
                        register={settlementDemandForm.register("accepted")}
                        label={<span>Accepted?</span>}
                      />
                      <div />
                      {matterSettlementDemandAcceptedFormValue && (
                        <>
                          <DateFormField
                            register={settlementDemandForm.register(
                              "acceptedDate",
                            )}
                            name="acceptedDate"
                            label="Accepted date*"
                          />
                          <div />
                        </>
                      )}
                    </div>
                  </div>
                )}
                {negotiationType === "Offer" && (
                  <>
                    {matterSettlementAcceptedFormValue ? (
                      <div className="border-muted bg-muted/40 col-span-full rounded-md border p-4">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <SwitchFormField
                            register={settlementDemandForm.register("accepted")}
                            label={<span>Accepted?</span>}
                            disabled={
                              isAnotherSettlementAccepted &&
                              // If the app gets in a state with multiple settlements accepted, this lets the users unselect an accepted settlement
                              !matterSettlementAcceptedFormValue
                            }
                          />
                          <div />
                          <DateFormField
                            register={settlementDemandForm.register(
                              "acceptedDate",
                            )}
                            name="acceptedDate"
                            label="Accepted date*"
                          />
                          <SimpleSelectFormField
                            label="Money status*"
                            placeholder="Select money status"
                            register={settlementDemandForm.register("status")}
                            items={MATTER_SETTLEMENT_MONEY_STATUS_OPTIONS}
                            formatItemLabel={(item) => item}
                            clearable
                          />
                          <SwitchFormField
                            register={settlementDemandForm.register("issues")}
                            label="Issues"
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div />
                        <SwitchFormField
                          register={settlementDemandForm.register("accepted")}
                          label={<span>Accepted?</span>}
                          disabled={
                            isAnotherSettlementAccepted &&
                            // If the app gets in a state with multiple settlements accepted, this lets the users unselect an accepted settlement
                            !matterSettlementAcceptedFormValue
                          }
                        />
                        <div />
                      </>
                    )}
                    {isAnotherSettlementAccepted && (
                      <div className="col-span-full">
                        <Typography
                          size="xs"
                          variant="subHeading"
                          className="flex items-center gap-1"
                        >
                          <AlertCircleIcon className="h-4 min-w-4" />
                          Another offer has already been accepted for this
                          insurance. You can only accept one offer per
                          insurance.
                        </Typography>
                      </div>
                    )}
                    <FormField
                      control={settlementDemandForm.control}
                      name="attorneyFeePercentage"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex justify-between">
                            <FormLabel>Attorney fee (%)</FormLabel>
                            <FormMessage />
                          </div>
                          <AttorneyFeeSelect
                            placeholder={`Select or enter an attorney fee`}
                            value={field.value?.toString() ?? ""}
                            onChange={(value) => {
                              settlementDemandForm.setValue(
                                "attorneyFeePercentage",
                                Number(value),
                                {
                                  shouldDirty: true,
                                },
                              );
                              if (
                                Number(matterSettlementAmountFormValue) &&
                                Number(matterSettlementAmountFormValue) > 0
                              ) {
                                settlementDemandForm.setValue(
                                  "attorneyFeeAmount",
                                  Number(
                                    calculateAttorneyFee(
                                      matterSettlementAmountFormValue ?? 0,
                                      value,
                                    ).toFixed(2),
                                  ),
                                  {
                                    shouldDirty: true,
                                  },
                                );
                              }
                            }}
                            options={MATTER_DEFAULT_ATTORNEY_FEE_OPTIONS}
                            createMessage={`Add an attorney fee`}
                            emptyMessage={`No attorney fee found`}
                            label={``}
                          />
                        </FormItem>
                      )}
                    />
                    <TooltipWrapper
                      message={
                        !matterSettlementAmountFormValue
                          ? "Enter an offer amount first."
                          : undefined
                      }
                    >
                      <div>
                        <CurrencyFormField
                          register={settlementDemandForm.register(
                            "attorneyFeeAmount",
                          )}
                          label="Attorney fee amount"
                          fixedDecimalScale
                          disabled={!matterSettlementAmountFormValue}
                          onChange={(value) => {
                            if (value) {
                              settlementDemandForm.setValue(
                                "attorneyFeePercentage",
                                Math.round(
                                  (Number(value) /
                                    (settlementDemandForm.watch("amount") ??
                                      0)) *
                                    100 *
                                    100,
                                ) / 100,
                                {
                                  shouldDirty: true,
                                },
                              );
                            }
                            settlementDemandForm.setValue(
                              "attorneyFeeAmount",
                              Number(value),
                              {
                                shouldDirty: true,
                              },
                            );
                          }}
                        />
                      </div>
                    </TooltipWrapper>
                  </>
                )}
                <div className="col-span-full">
                  <TextAreaFormField
                    register={settlementDemandForm.register(
                      negotiationType === "Demand" ? "details" : "details",
                    )}
                    label="Details"
                  />
                </div>
              </fieldset>
              {negotiationType === "Demand" && editing && (
                <fieldset
                  className={cn(
                    slideoverFieldsetClassName,
                    "mt-2 grid-cols-1 gap-4 sm:grid-cols-1",
                  )}
                >
                  {isReorderMode && (
                    <Typography size="xs" className="text-muted-foreground">
                      Drag documents to set their order, then save.
                    </Typography>
                  )}
                  {selectedDemandId ? (
                    <>
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <Typography variant="subHeading" size="sm">
                              Title page
                            </Typography>
                            <Typography
                              size="xs"
                              className="text-muted-foreground"
                            >
                              This document appears first in the demand package.
                            </Typography>
                          </div>
                          <div className="flex">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="rounded-r-none border-r-0"
                              disabled={
                                isReorderMode ||
                                isSyncingPackageSegments ||
                                !canCreateDemandPackageSegments ||
                                (Boolean(titlePageDocument) &&
                                  !canDeleteDemandPackageSegments)
                              }
                              onClick={() => {
                                setIsSelectTitlePageSlideoverOpen(true);
                              }}
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              {titlePageDocument
                                ? "Change title page"
                                : "Add title page"}
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="rounded-l-none px-2"
                                  aria-label="More title page actions"
                                  disabled={
                                    isReorderMode ||
                                    isSyncingPackageSegments ||
                                    !canCreateDemandPackageSegments
                                  }
                                >
                                  <ChevronDown className="h-4 w-4" />
                                  <span className="sr-only">
                                    Title page action options
                                  </span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={handleGenerateDocument}
                                  disabled={
                                    isGenerateDocumentLoading ||
                                    isReorderMode ||
                                    isSyncingPackageSegments
                                  }
                                >
                                  <WandSparkles className="mr-2 h-4 w-4" />
                                  Generate document
                                </DropdownMenuItem>
                                {canUseFirmFiles && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setIsSelectFirmFileTitlePageSlideoverOpen(
                                        true,
                                      );
                                    }}
                                    disabled={
                                      isReorderMode || isSyncingPackageSegments
                                    }
                                  >
                                    <FilesIcon className="mr-2 h-4 w-4" />
                                    Use firm file
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                        <div className="relative">
                          {isSyncingTitlePage && (
                            <div className="bg-background/95 absolute inset-0 z-10 flex items-center justify-center rounded-md">
                              <LoadingSpinner />
                            </div>
                          )}
                          {titlePageDocument ? (
                            <div
                              className={cn(
                                "rounded-md border",
                                isSyncingTitlePage &&
                                  "pointer-events-none opacity-50",
                              )}
                            >
                              <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                                <div className="min-w-0 flex-1">
                                  <Typography
                                    weight="medium"
                                    className="truncate"
                                  >
                                    {titlePageFile?.name ?? "File unavailable"}
                                  </Typography>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      if (!titlePageFile) return;
                                      openFileModal({
                                        matterId: matter.id,
                                        activeFileId: titlePageFile.id,
                                        editing: true,
                                        currentFolderId:
                                          titlePageFile.idLocation || "",
                                        isFolder:
                                          titlePageFile.isFolder ?? false,
                                      });
                                    }}
                                    aria-label="Open file"
                                    disabled={
                                      !canViewMatterFiles ||
                                      !titlePageFile ||
                                      isSyncingPackageSegments
                                    }
                                  >
                                    <SquareArrowOutUpRight className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      if (!titlePageDocument.segmentId) {
                                        toast.error(
                                          "Save the settlement demand before removing documents.",
                                        );
                                        return;
                                      }
                                      setExhibitPendingDelete({
                                        segmentId: titlePageDocument.segmentId,
                                        name: titlePageFile?.name,
                                        type: "title",
                                      });
                                    }}
                                    aria-label="Remove title page"
                                    disabled={
                                      !titlePageDocument.segmentId ||
                                      isReorderBusy ||
                                      !canDeleteDemandPackageSegments
                                    }
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div
                              className={cn(
                                "text-muted-foreground rounded-md border border-dashed px-4 py-6 text-center text-sm",
                                isSyncingTitlePage &&
                                  "pointer-events-none opacity-50",
                              )}
                            >
                              No title page selected yet.
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 space-y-2">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <Typography variant="subHeading">
                              Select documents
                            </Typography>
                            <Typography
                              size="xs"
                              className="text-muted-foreground"
                            >
                              Choose documents to include in the demand package
                              and organize their order.
                            </Typography>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="flex">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="rounded-r-none border-r-0"
                                disabled={
                                  isReorderMode ||
                                  isSyncingPackageSegments ||
                                  !canCreateDemandPackageSegments
                                }
                                onClick={() => {
                                  setIsSelectExhibitSlideoverOpen(true);
                                }}
                              >
                                <Plus className="mr-2 h-4 w-4" />
                                Add documents
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="rounded-l-none px-2"
                                    aria-label="More exhibit actions"
                                    disabled={
                                      isReorderMode ||
                                      isSyncingPackageSegments ||
                                      !canCreateDemandPackageSegments
                                    }
                                  >
                                    <ChevronDown className="h-4 w-4" />
                                    <span className="sr-only">
                                      Exhibit action options
                                    </span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={handleGenerateDocument}
                                    disabled={
                                      isGenerateDocumentLoading ||
                                      isReorderMode ||
                                      isSyncingPackageSegments
                                    }
                                  >
                                    <WandSparkles className="mr-2 h-4 w-4" />
                                    Generate document
                                  </DropdownMenuItem>
                                  {canUseFirmFiles && (
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setIsSelectFirmFileExhibitSlideoverOpen(
                                          true,
                                        );
                                      }}
                                      disabled={
                                        isReorderMode ||
                                        isSyncingPackageSegments
                                      }
                                    >
                                      <FilesIcon className="mr-2 h-4 w-4" />
                                      Add firm file
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                            {demandPackageExhibits.length > 1 && (
                              <div className="flex items-center gap-2">
                                {isReorderMode ? (
                                  <>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={handleCancelReorder}
                                      disabled={isReorderBusy}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      onClick={handleSaveReorderedExhibits}
                                      disabled={
                                        isReorderBusy ||
                                        !hasReorderChanges ||
                                        !canUpdateDemandPackageSegments
                                      }
                                    >
                                      {isSavingReorder ? (
                                        <>
                                          <LoadingSpinner className="mr-2 h-4 w-4" />
                                          Saving...
                                        </>
                                      ) : (
                                        "Save order"
                                      )}
                                    </Button>
                                  </>
                                ) : (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={handleStartReorder}
                                    disabled={
                                      isReorderBusy ||
                                      !selectedDemandId ||
                                      !canUpdateDemandPackageSegments
                                    }
                                  >
                                    Reorder
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {demandPackageExhibits.length > 0 ? (
                          <div className="relative">
                            {isSyncingExhibits && (
                              <div className="bg-background/95 absolute inset-0 z-10 flex items-center justify-center rounded-md">
                                <LoadingSpinner />
                              </div>
                            )}
                            <div
                              className={cn(
                                "rounded-md border",
                                isSyncingExhibits &&
                                  "pointer-events-none opacity-50",
                              )}
                            >
                              <div className="flex items-center justify-between border-b px-4 py-2 text-sm font-medium">
                                <span>Demand package documents</span>
                                <span className="text-muted-foreground">
                                  {documentCount}{" "}
                                  {documentCount === 1
                                    ? "document"
                                    : "documents"}
                                </span>
                              </div>
                              <div className="divide-y">
                                <DndContext
                                  sensors={reorderSensors}
                                  onDragEnd={handleReorderDragEnd}
                                >
                                  <SortableContext
                                    items={demandPackageExhibitIds}
                                    strategy={verticalListSortingStrategy}
                                  >
                                    {displayedExhibits.map((exhibit, index) => {
                                      const file = matter.files.find(
                                        (matterFile) =>
                                          matterFile.id === exhibit.fileId,
                                      );
                                      return (
                                        <DemandPackageExhibitItem
                                          key={exhibit.clientId}
                                          exhibit={exhibit}
                                          index={index}
                                          file={file}
                                          isReorderMode={isReorderMode}
                                          isBusy={isReorderBusy}
                                          canOpenFile={canViewMatterFiles}
                                          canRemove={
                                            canDeleteDemandPackageSegments
                                          }
                                          onOpenFile={() => {
                                            if (!file) return;
                                            openFileModal({
                                              matterId: matter.id,
                                              activeFileId: file.id,
                                              editing: true,
                                              currentFolderId:
                                                file.idLocation || "",
                                              isFolder: file.isFolder ?? false,
                                            });
                                          }}
                                          onRequestRemove={() => {
                                            if (!exhibit.segmentId) {
                                              toast.error(
                                                "Save the settlement demand before removing documents.",
                                              );
                                              return;
                                            }
                                            setExhibitPendingDelete({
                                              segmentId: exhibit.segmentId,
                                              name: file?.name,
                                              type: "exhibit",
                                            });
                                          }}
                                        />
                                      );
                                    })}
                                  </SortableContext>
                                </DndContext>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-muted-foreground rounded-md border border-dashed px-4 py-6 text-center text-sm">
                            No exhibits selected yet.
                          </div>
                        )}
                      </div>
                      <div className="mt-4 flex justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={openGenerateDemandPackageSlideover}
                          disabled={
                            isGeneratingDemandPackage ||
                            demandPackageExhibits.length === 0
                          }
                        >
                          {isGeneratingDemandPackage ? (
                            <>
                              <LoadingSpinner className="mr-2 h-4 w-4" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <WandSparkles className="mr-2 h-4 w-4" />
                              Generate demand package
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="p-4">
                      <div className="text-muted-foreground rounded-md border border-dashed px-4 py-6 text-center text-sm">
                        Save the settlement demand to add exhibits.
                      </div>
                    </div>
                  )}
                </fieldset>
              )}
              {negotiationType === "Demand" && !editing && (
                <div className="p-4">
                  <div className="text-muted-foreground mt-2 rounded-md border border-dashed px-4 py-6 text-sm">
                    Save the settlement demand to add exhibits.
                  </div>
                </div>
              )}
            </div>
          </form>
        </Form>
      </AppModal>

      {negotiationType === "Demand" && editing && (
        <>
          <SelectDocumentModal
            open={isSelectExhibitSlideoverOpen}
            setOpen={(open) => {
              if (!open) {
                setIsSelectExhibitSlideoverOpen(false);
                return;
              }
              if (isSyncingPackageSegments) {
                return;
              }
              setIsSelectExhibitSlideoverOpen(true);
            }}
            matter={matter}
            onDocumentSelected={handleDocumentSelection}
            multiple
            excludedFileIds={
              demandPackageExhibits.length > 0
                ? new Set(
                    demandPackageExhibits.map((exhibit) => exhibit.fileId),
                  )
                : undefined
            }
            title="Select exhibits"
            description="Choose documents to include as exhibits."
            enabledFileTypes={allowedExhibitFileTypes}
          />
          <SelectDocumentModal
            open={isSelectTitlePageSlideoverOpen}
            setOpen={(open) => {
              if (!open) {
                setIsSelectTitlePageSlideoverOpen(false);
                return;
              }
              if (isSyncingPackageSegments) {
                return;
              }
              setIsSelectTitlePageSlideoverOpen(true);
            }}
            matter={matter}
            onDocumentSelected={handleTitlePageSelection}
            multiple={false}
            excludedFileIds={
              demandPackageExhibits.length > 0 || titlePageDocument?.fileId
                ? new Set(
                    [
                      ...demandPackageExhibits.map((exhibit) => exhibit.fileId),
                      titlePageDocument?.fileId,
                    ].filter(Boolean) as string[],
                  )
                : undefined
            }
            title="Select title page"
            description="Choose a single document to use as the title page."
            enabledFileTypes={allowedExhibitFileTypes}
          />
          <SelectFirmFileModal
            open={isSelectFirmFileExhibitSlideoverOpen}
            setOpen={(open) => {
              if (!open) {
                setIsSelectFirmFileExhibitSlideoverOpen(false);
                return;
              }
              if (isSyncingPackageSegments) {
                return;
              }
              setIsSelectFirmFileExhibitSlideoverOpen(true);
            }}
            onFirmFilesSelected={(files) => handleFirmFileSelection(files)}
            multiple
            title="Select firm files"
            description="Choose firm files to include as exhibits."
            enabledFileTypes={allowedExhibitFileTypes}
          />
          <SelectFirmFileModal
            open={isSelectFirmFileTitlePageSlideoverOpen}
            setOpen={(open) => {
              if (!open) {
                setIsSelectFirmFileTitlePageSlideoverOpen(false);
                return;
              }
              if (isSyncingPackageSegments) {
                return;
              }
              setIsSelectFirmFileTitlePageSlideoverOpen(true);
            }}
            onFirmFilesSelected={(files) =>
              handleFirmFileSelection(files, { isTitlePage: true })
            }
            multiple={false}
            title="Select firm file"
            description="Choose a firm file to use as the title page."
            enabledFileTypes={allowedExhibitFileTypes}
          />
          <ActionConfirmationModal
            open={Boolean(exhibitPendingDelete)}
            setOpen={(open) => {
              if (!open) {
                setExhibitPendingDelete(null);
              }
            }}
            title={
              exhibitPendingDelete?.type === "title"
                ? "Remove title page?"
                : "Remove document?"
            }
            description={
              exhibitPendingDelete?.type === "title"
                ? `Are you sure you want to remove ${pendingDeleteName} as the title page?`
                : `Are you sure you want to remove ${pendingDeleteName} from the demand package?`
            }
            buttonText="Remove"
            afterActionFn={() => {
              if (exhibitPendingDelete) {
                handleRemoveDocument(exhibitPendingDelete.segmentId, {
                  isTitlePage: exhibitPendingDelete.type === "title",
                });
              }
              setExhibitPendingDelete(null);
            }}
            cancelText="Cancel"
            actionVariant="destructive"
          />
          <AppModal
            open={isGeneratePackageSlideoverOpen}
            onOpenChange={(open) => {
              if (isGeneratingDemandPackage) return;
              setIsGeneratePackageSlideoverOpen(open);
              if (!open) {
                generatePackageForm.reset({
                  packageName: `Demand Package - ${dayjs().format(
                    "MMM D YYYY",
                  )}`,
                });
              }
            }}
            title="Generate demand package"
            description="Name the merged document before generating it."
            isDirty={isGeneratePackageDirty}
            size="xl"
            footer={
              <div className="flex justify-end gap-2">
                <FormCancelButton
                  setOpen={(open) => {
                    if (isGeneratingDemandPackage) return;
                    setIsGeneratePackageSlideoverOpen(open);
                    if (!open) {
                      generatePackageForm.reset({
                        packageName: `Demand Package - ${dayjs().format(
                          "MMM D YYYY",
                        )}`,
                      });
                    }
                  }}
                  isDirty={isGeneratePackageDirty}
                />
                <Button
                  type="submit"
                  form={generatePackageFormId}
                  disabled={isGeneratingDemandPackage}
                >
                  {isGeneratingDemandPackage ? (
                    <>
                      <LoadingSpinner className="mr-2 h-4 w-4" />
                      Generating...
                    </>
                  ) : (
                    "Generate"
                  )}
                </Button>
              </div>
            }
          >
            <Form {...generatePackageForm}>
              <form
                id={generatePackageFormId}
                className="relative flex grow flex-col overflow-hidden"
                onSubmit={async (event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  await generatePackageForm.handleSubmit(
                    handleGeneratePackageSubmit,
                  )();
                }}
              >
                {isGeneratingDemandPackage && <FormLoadingOverlay />}
                <div className="overflow-y-auto">
                  <fieldset
                    className={cn(
                      slideoverFieldsetClassName,
                      "grid-cols-1 md:grid-cols-1",
                    )}
                  >
                    <TextFormField
                      register={generatePackageForm.register("packageName")}
                      label="Package name"
                      disabled={isGeneratingDemandPackage}
                      className="col-span-full"
                    />
                  </fieldset>
                </div>
              </form>
            </Form>
          </AppModal>
        </>
      )}

      {editing && selectedSettlement && (
        <ActionConfirmationModal
          open={isDeleting}
          setOpen={setIsDeleting}
          title={`Are you sure you want to delete this settlement ${negotiationType.toLowerCase()}?`}
          description={`This action cannot be undone. This will remove the settlement ${negotiationType.toLowerCase()} and all associated tasks and files.`}
          afterActionFn={handleDelete}
        />
      )}

      {/* {providerContactId && (
        <EditContactModal
          activeContactId={providerContactId}
          open={isEditingContact}
          setOpen={setIsEditingContact}
          hasEditPermission={hasActionPermission(
            ACTION_PERMISSION_KEYS.contact,
            ACTION_PERMISSION_VALUES.update,
          )}
        />
      )} */}
    </>
  );
};

type DemandPackageExhibitItemProps = {
  exhibit: DemandPackageExhibitFormValue;
  index: number;
  file?: MatterFile;
  isReorderMode: boolean;
  isBusy: boolean;
  canOpenFile: boolean;
  canRemove: boolean;
  onOpenFile: () => void;
  onRequestRemove: () => void;
};

const DemandPackageExhibitItem = ({
  exhibit,
  index,
  file,
  isReorderMode,
  isBusy,
  canOpenFile,
  canRemove,
  onOpenFile,
  onRequestRemove,
}: DemandPackageExhibitItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: exhibit.clientId,
      disabled: !isReorderMode || isBusy,
    });

  const style = isReorderMode
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn("flex flex-col gap-3 px-4 py-3")}
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-muted-foreground text-sm font-semibold">
          {index + 1}
        </div>
        {isReorderMode && (
          <button
            type="button"
            className={cn(
              "text-muted-foreground flex h-7 w-7 items-center justify-center rounded-md border",
              isBusy
                ? "cursor-not-allowed opacity-50"
                : "bg-background hover:bg-muted cursor-grab",
            )}
            aria-label="Reorder demand document"
            disabled={isBusy}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <Typography weight="medium" className="truncate">
            {file?.name ?? "File unavailable"}
          </Typography>
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onOpenFile}
            aria-label="Open file"
            disabled={!canOpenFile || !file || isBusy}
          >
            <SquareArrowOutUpRight className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRequestRemove}
            aria-label="Remove exhibit"
            disabled={!exhibit.segmentId || isBusy || !canRemove}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
